import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, RefreshCw, ShieldCheck, Building2, FileDown } from "lucide-react";

export default function MemberPass() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [qrKey, setQrKey] = useState(Date.now());

  const load = () => {
    setErr("");
    api.get("/member/me/pass")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Impossible de charger votre pass."));
  };

  useEffect(() => { load(); }, []);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const qrUrl = `${backend}/api/member/me/pass.png?v=${qrKey}`;

  const downloadPng = async () => {
    try {
      const res = await fetch(qrUrl, { credentials: "include" });
      if (!res.ok) throw new Error("Erreur téléchargement");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `pass-proxireduc-${data?.member?.last_name || "membre"}.png`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) {
      toast.error("Téléchargement impossible.");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${backend}/api/member/me/pass.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Erreur");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `pass-proxireduc-${data?.member?.last_name || "membre"}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) {
      toast.error("Téléchargement PDF impossible.");
    }
  };

  if (err) {
    return (
      <div className="text-center py-12" data-testid="member-pass-error">
        <div className="text-rose-700 font-semibold mb-1">Pass indisponible</div>
        <p className="text-slate-600 text-sm">{err}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="text-center text-slate-500 py-12">Chargement de votre pass…</div>;
  }

  const { member, organization } = data;

  return (
    <div data-testid="member-pass-page" className="space-y-5">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
          <ShieldCheck size={12} /> Pass numérique actif
        </div>
        <h1 className="mt-3 font-heading text-2xl font-extrabold text-slate-900">
          Mon Pass PROXIreduc
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Présentez ce QR code au commerçant partenaire pour bénéficier de vos avantages.
        </p>
      </div>

      {/* Carte Pass */}
      <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200 bg-white">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-[#E05638] p-5 text-white">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest opacity-90">
            <Building2 size={14} /> {organization.type_label || "Organisme"}
          </div>
          <div className="mt-1 font-heading text-lg font-extrabold leading-tight">
            {organization.name}
          </div>
          {organization.city && (
            <div className="text-[11px] opacity-80 mt-0.5">{organization.city}</div>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Membre</div>
              <div className="mt-0.5 font-heading text-xl font-extrabold text-slate-900 truncate">
                {member.first_name} {member.last_name}
              </div>
              {member.internal_ref && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Réf. interne : <span className="font-mono text-slate-700">{member.internal_ref}</span>
                </div>
              )}
              {member.join_date && (
                <div className="text-[11px] text-slate-500">
                  Adhérent depuis : {member.join_date}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 p-3 bg-white rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center">
            <img
              key={qrKey}
              src={qrUrl}
              alt="QR Code de votre pass"
              className="w-56 h-56 object-contain"
              data-testid="member-pass-qr"
              onError={() => toast.error("QR indisponible")}
            />
          </div>

          <div className="mt-3 flex gap-2">
            <Button
              variant="outline" size="sm" onClick={() => setQrKey(Date.now())}
              className="flex-1" data-testid="member-pass-refresh"
            >
              <RefreshCw size={14} className="mr-1.5" /> Actualiser
            </Button>
            <Button
              size="sm" onClick={downloadPng}
              className="flex-1 bg-slate-900 hover:bg-slate-800"
              data-testid="member-pass-download"
            >
              <Download size={14} className="mr-1.5" /> Télécharger
            </Button>
          </div>
          <Button
            size="sm" variant="outline" onClick={downloadPdf}
            className="mt-2 w-full border-[#E05638] text-[#E05638] hover:bg-orange-50"
            data-testid="member-pass-download-pdf"
          >
            <FileDown size={14} className="mr-1.5" /> Télécharger mon pass en PDF
          </Button>
        </div>
      </div>

      {/* Comment ça marche */}
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="font-heading font-extrabold text-sm text-slate-900 mb-2">Comment l'utiliser ?</div>
        <ol className="text-sm text-slate-700 space-y-1.5 list-decimal list-inside">
          <li>Rendez-vous chez un commerce partenaire PROXIreduc.</li>
          <li>Présentez ce QR code (écran ou impression).</li>
          <li>Le commerçant le scanne et confirme votre statut de membre.</li>
          <li>Bénéficiez de vos réductions et avantages négociés.</li>
        </ol>
      </div>

      <p className="text-center text-[11px] text-slate-400">
        Ce pass est strictement personnel. Ne le partagez pas.
      </p>
    </div>
  );
}
